import React, { useState, useEffect } from "react";
import { Box, useTheme, InputBase, IconButton } from "@mui/material";
import { DataGrid, GridToolbar } from "@mui/x-data-grid";
import { tokens } from "../../theme";
import Header from "../../components/Header";
import SearchIcon from "@mui/icons-material/Search";
import { getDatosAutoClaveView } from "../../services/autoClave.services";
import MenuIcon from "@mui/icons-material/Menu";
import Menu from "@mui/material/Menu";
import MenuItem from "@mui/material/MenuItem";

const AutoClaveView = () => {
  const [data, setData] = useState([]); // Datos de grupos
  const [searchValue, setSearchValue] = useState(""); // Valor del campo de búsqueda
  const [filteredData, setFilteredData] = useState([]); // Datos filtrados para la tabla
  const [anchorEl, setAnchorEl] = useState(null);
  const [selectedRow, setSelectedRow] = useState(null);

  const theme = useTheme();
  const colors = tokens(theme.palette.mode);

  const handleMenuClick = (event, row) => {
    setAnchorEl(event.currentTarget);
    setSelectedRow(row);
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
    setSelectedRow(null);
  };

  const handleSalida = () => {
    
    handleMenuClose();
  };


  // Definir columnas de la tabla
  const columns = [
    {
      field: "menu",
      headerName: "",
      flex: 0.2,
      renderCell: (params) => (
        <IconButton onClick={(event) => handleMenuClick(event, params.row)}>
          <MenuIcon />
        </IconButton>
      ),
    },
    { field: "id", headerName: "ID", flex: 0.1 },
    {
      field: "estado",
      headerName: "Estado",
      flex: 0.5,
      type: "string",
      headerAlign: "left",
      align: "left",
    },
    {
      field: "primerCaballete",
      headerName: "Primer Caballete",
      flex: 0.75,
      type: "string",
      headerAlign: "left",
      align: "left",
    },
    {
      field: "segundoCaballete",
      headerName: "Segundo Caballete",
      flex: 0.75,
      cellClassName: "name-column--cell",
    },
    {
      field: "recetaUtilizada",
      headerName: "Receta Utilizada",
      flex: 1,
      cellClassName: "name-column--cell",
    },
    {
      field: "tempAguaCaja1",
      headerName: "Caja 1",
      flex: 0.3,
      cellClassName: "name-column--cell",
      type: "number",
    },
    {
      field: "tempAguaCaja2",
      headerName: "Caja 2",
      flex: 0.3,
      cellClassName: "name-column--cell",
      type: "number",
    },
    {
      field: "tempAguaCaja3",
      headerName: "Caja 3",
      flex: 0.3,
      cellClassName: "name-column--cell",
      type: "number",
    },
    {
      field: "tempInterna",
      headerName: "Temperatura Interna",
      flex: 0.75,
      type: "number",
      headerAlign: "center",
      align: "center",
    },
    {
      field: "tempExterna",
      headerName: "Temperatura Externa",
      flex: 0.75,
      type: "number",
      headerAlign: "center",
      align: "center",
    },
    {
      field: "create_at",
      headerName: "Fecha de Entrada",
      flex: 0.75,
      cellClassName: "name-column--cell",
      valueFormatter: (params) => {
        if (!params.value) return "";
        const d = new Date(params.value);
        const pad = (n) => String(n).padStart(2, "0");
        return `${pad(d.getDate())}/${pad(d.getMonth() + 1)}/${d.getFullYear()} ${pad(d.getHours())}:${pad(d.getMinutes())}`;
      },
    },
    {
      field: "update_at",
      headerName: "Fecha de Salida",
      flex: 0.75,
      cellClassName: "name-column--cell",
      valueFormatter: (params) => {
        if (!params.value) return "";
        const d = new Date(params.value);
        const pad = (n) => String(n).padStart(2, "0");
        return `${pad(d.getDate())}/${pad(d.getMonth() + 1)}/${d.getFullYear()} ${pad(d.getHours())}:${pad(d.getMinutes())}`;
      },
    },
    {
      field: "autorizacion",
      headerName: "Autorizacion",
      flex: 0.75,
      type: "number",
      headerAlign: "center",
      align: "center",
    },
  ];

  // Obtener datos de los grupos al cargar el componente
  useEffect(() => {
    const fetchAutoClave = async () => {
      try {
        const response = await getDatosAutoClaveView();
        setData(response);
        setFilteredData(response);
      } catch (error) {
        console.error("Error al obtener los datos del autoclave del backend:", error);
      }
    };

    fetchAutoClave(); // Llamar a la función que obtiene los datos del autoclave
  }, []);

  // Manejar la búsqueda en la tabla
  const handleSearchChange = (event) => {
    const { value } = event.target;
    setSearchValue(value);

    // Filtrar los datos en base al valor de búsqueda
    const filtered = data.filter((item) =>
      item.cod.toLowerCase().includes(value.toLowerCase()) 
      );
    
    setFilteredData(filtered);
  };

  return (
    <Box m="20px">
      <Box display="flex" justifyContent="space-between" p={0}>
        <Header title="Historial Registro AutoClave"/>
        <Box display="flex" alignItems="center">
          <Box
            display="flex"
            backgroundColor={colors.primary[400]}
            borderRadius="3px"
            height={"40%"}
            width={"90%"}
            ml={1}
          >
            <InputBase
              sx={{ ml: 2, flex: 1 }}
              placeholder="Buscar"
              value={searchValue}
              onChange={handleSearchChange}
            />
            <IconButton type="button" sx={{ p: 1 }}>
              <SearchIcon />
            </IconButton>
          </Box>
        </Box>
      </Box>
      <Box
        m="40px 0 0 0"
        height="75vh"
        sx={{
          "& .MuiDataGrid-root": {
            border: "none",
          },
          "& .MuiDataGrid-cell": {
            borderBottom: "none",
          },
          "& .name-column--cell": {
            color: colors.greenAccent[300],
          },
          "& .MuiDataGrid-columnHeaders": {
            backgroundColor: colors.blueAccent[900],
            borderBottom: "none",
          },
          "& .MuiDataGrid-virtualScroller": {
            backgroundColor: colors.primary[400],
          },
          "& .MuiDataGrid-footerContainer": {
            borderTop: "none",
            backgroundColor: colors.blueAccent[900],
          },
          "& .MuiCheckbox-root": {
            color: `${colors.greenAccent[200]} !important`,
          },
          "& .MuiDataGrid-toolbarContainer .MuiButton-text": {
            color: `${colors.grey[100]} !important`,
          },
        }}
      >
        <DataGrid
          rows={filteredData}
          getRowId={(row) => row.id} // Acceder correctamente al campo `Id`
          columns={columns}
          components={{ Toolbar: GridToolbar }}
          disableMultipleSelection={true}
        />
      </Box>
      <Box>
        <Menu
          anchorEl={anchorEl}
          open={Boolean(anchorEl)}
          onClose={handleMenuClose}
        >
          <MenuItem onClick={handleSalida}>Autorizacion</MenuItem>
        </Menu>
      </Box>
    </Box>
  );
};

export default AutoClaveView;